// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Reel Mapper
// Maps provably fair hash values to reel symbol positions
// ================================================================

// Symbol definitions — each slot game defines its own symbols
// Default symbol set for Forge-themed slots
const DEFAULT_SYMBOLS = [
  { id: 'forge',    name: 'Forge',       emoji: '🔥', weight: 2,  payout: { 3: 50, 4: 200, 5: 1000 } },
  { id: 'anvil',    name: 'Anvil',       emoji: '⚒️',  weight: 3,  payout: { 3: 25, 4: 100, 5: 500 } },
  { id: 'hammer',   name: 'Hammer',      emoji: '🔨', weight: 4,  payout: { 3: 15, 4: 60, 5: 250 } },
  { id: 'diamond',  name: 'Diamond',     emoji: '💎', weight: 5,  payout: { 3: 10, 4: 40, 5: 150 } },
  { id: 'gold',     name: 'Gold Bar',    emoji: '🥇', weight: 6,  payout: { 3: 8, 4: 30, 5: 100 } },
  { id: 'star',     name: 'Star',        emoji: '⭐',       weight: 8,  payout: { 3: 5, 4: 20, 5: 60 } },
  { id: 'coin',     name: 'Coin',        emoji: '🪙', weight: 10, payout: { 3: 3, 4: 10, 5: 30 } },
  { id: 'cherry',   name: 'Cherry',      emoji: '🍒', weight: 12, payout: { 3: 2, 4: 5, 5: 15 } },
  { id: 'bar',      name: 'Bar',         emoji: '🍺', weight: 15, payout: { 3: 1, 4: 3, 5: 8 } },
];

// Wild symbol — substitutes for any symbol
const WILD_SYMBOL = { id: 'wild', name: 'Wild', emoji: '🎰', weight: 1, isWild: true };

// Scatter symbol — triggers bonus features
const SCATTER_SYMBOL = { id: 'scatter', name: 'Scatter', emoji: '🌟', weight: 2, isScatter: true };

/**
 * Build weighted reel strip from symbols
 * Each symbol appears proportional to its weight
 */
function buildReelStrip(symbols) {
  const strip = [];
  for (const sym of symbols) {
    for (let i = 0; i < sym.weight; i++) {
      strip.push(sym);
    }
  }
  return strip;
}

/**
 * Map a float value (0-1) to a symbol on the reel strip
 * @param {number} value - Float from 0 to 1 (from ProvablyFairEngine)
 * @param {Array} reelStrip - Weighted reel strip
 * @returns {Object} The selected symbol
 */
function mapToSymbol(value, reelStrip) {
  const index = Math.floor(value * reelStrip.length) % reelStrip.length;
  return reelStrip[index];
}

/**
 * Map multiple float values to a full spin result
 * @param {Array<number>} values - Array of floats from ProvablyFairEngine
 * @param {number} reels - Number of reels (columns)
 * @param {number} rows - Number of visible rows per reel
 * @param {Array} symbols - Symbol definitions to use
 * @returns {Array<Array<Object>>} 2D array [reel][row] of symbols
 */
function mapToGrid(values, reels = 3, rows = 3, symbols = null) {
  const allSymbols = symbols || [...DEFAULT_SYMBOLS, WILD_SYMBOL, SCATTER_SYMBOL];
  const reelStrip = buildReelStrip(allSymbols);
  const grid = [];

  for (let r = 0; r < reels; r++) {
    const reelResult = [];
    for (let row = 0; row < rows; row++) {
      // Use different parts of the hash for each position
      const valueIndex = r * rows + row;
      // Cycle through values if we need more positions than values available
      const value = values[valueIndex % values.length];
      // Add offset based on position to ensure different results even with same base value
      const adjustedValue = (value + (r * 0.1371 + row * 0.2749)) % 1;
      reelResult.push(mapToSymbol(adjustedValue, reelStrip));
    }
    grid.push(reelResult);
  }

  return grid;
}

/**
 * Get the reel strip index for animation purposes
 */
function getReelStopPosition(value, reelStrip) {
  return Math.floor(value * reelStrip.length) % reelStrip.length;
}

export {
  DEFAULT_SYMBOLS,
  WILD_SYMBOL,
  SCATTER_SYMBOL,
  buildReelStrip,
  mapToSymbol,
  mapToGrid,
  getReelStopPosition,
};
